/**
 * Custom Swiper modules.
 *
 * Defines custom Swiper plugin modules and exposes them on `window.swiperModules`
 * so they can be imported and passed to the Swiper `modules` option.
 *
 * Exposed modules:
 *   expandModule - A custom carousel effect where the active slide expands to
 *                  `activeWidth` while inactive slides remain at `slideWidth`.
 *                  Adjacent slides animate progressively as the user drags
 *                  between them. Also sets `--swiper-progress` on every slide
 *                  (1 = fully active, 0 = fully inactive) so CSS transitions
 *                  can be driven by the property regardless of whether the
 *                  expand size effect is enabled.
 *
 * @example
 *   import Swiper from 'swiper';
 *
 *   const { expandModule } = window.swiperModules;
 *
 *   new Swiper('.swiper', {
 *     modules: [expandModule],
 *     expand: {
 *       slideWidth: 334,
 *       activeWidth: 676,
 *     },
 *   });
 */
(() => {
  /**
   * Swiper expand module.
   *
   * Registers an `expand` option on the Swiper instance. When enabled, the
   * inner `.swiper-expand-wrapper` element of each slide is resized and
   * repositioned via inline styles so the active slide appears wider than its
   * neighbours while all wrappers remain visually adjacent.
   *
   * The module also writes `--swiper-progress` (range 0–1) as a CSS custom
   * property on every slide element on every translate tick, regardless of
   * whether the `expand` size effect is active.
   *
   * @param {object} options - Swiper plugin context provided by Swiper core.
   * @param {import('swiper').Swiper} options.swiper - The Swiper instance.
   * @param {Function} options.extendParams - Registers additional Swiper params.
   * @param {Function} options.on - Registers a Swiper event listener.
   *
   * @expand_options
   *   slideWidth  - Pixel width of inactive slides' inner wrapper.
   *   activeWidth - Pixel width of the active slide's inner wrapper.
   *
   * @css_properties set on slide elements
   *   --swiper-progress - 1 when the slide is fully active, 0 when fully
   *                       inactive. Values between 0 and 1 occur during drag.
   *
   * @example
   *   new Swiper('.swiper', {
   *     modules: [expandModule],
   *     expand: {
   *       slideWidth: 334,
   *       activeWidth: 676,
   *     },
   *   });
   *
   *   // Responsive: enable expand only above 768 px
   *   new Swiper('.swiper', {
   *     modules: [expandModule],
   *     expand: false,
   *     breakpoints: {
   *       768: {
   *         expand: { slideWidth: 334, activeWidth: 676 },
   *       },
   *     },
   *   });
   */
  const expandModule = ({ swiper, extendParams, on }) => {
    extendParams({
      expand: false,
    });

    /** CSS selector for the inner wrapper element that receives size/position styles. */
    const WRAPPER_SELECTOR = ".swiper-expand-wrapper";

    /**
     * Writes `--swiper-progress` (0–1) onto every slide element.
     * 1 = fully active, 0 = fully inactive. Values between 0 and 1 occur
     * during a drag between two slides.
     */
    const setProgress = () => {
      const { slides } = swiper;

      for (let i = 0; i < slides.length; i++) {
        const absProgress = Math.abs(slides[i].progress);
        const activeProgress = 1 - Math.min(absProgress, 1);
        slides[i].style.setProperty("--swiper-progress", activeProgress);
      }
    };

    /**
     * Runs on every translate tick. Calls `setProgress` unconditionally, then
     * — when `expand` is configured — computes each inner wrapper's width and
     * X-offset so all wrappers remain visually adjacent while the active one
     * appears wider.
     *
     * Width interpolation: `activeWidth` at progress 0 (active slide),
     * `slideWidth` at progress ±1 (fully inactive).
     *
     * Offset logic closes the cumulative gap introduced by widening:
     *   - Dead zone [−1, 0]: no offset (active / transition area).
     *   - Slides to the left (progress > 0): shifted right (+).
     *   - Slides to the right (progress < −1): shifted left (−).
     */
    const setTranslate = () => {
      setProgress();

      if (!swiper.params.expand) return;

      const { slideWidth, activeWidth } = swiper.params.expand;
      const { slides } = swiper;

      for (let i = 0; i < slides.length; i++) {
        const slideEl = slides[i];
        const progress = slideEl.progress;
        const absProgress = Math.abs(progress);
        const t = Math.min(absProgress, 1);

        // Width: activeWidth at t=0 (active), slideWidth at t=1 (inactive)
        const width = activeWidth + (slideWidth - activeWidth) * t;

        // Close cumulative gaps so all wrappers appear adjacent.
        // Dead zone [-1, 0]: no offset (active/transition area).
        // Left slides (progress > 0): shift right (+).
        // Right slides (progress < -1): shift left (-).
        const gap = activeWidth - slideWidth;
        const offset =
          (Math.min(progress + 1, 0) + Math.max(progress, 0)) * gap;

        // Inline styles on the inner wrapper for width + position
        const wrapperEl = slideEl.querySelector(WRAPPER_SELECTOR);
        if (wrapperEl) {
          wrapperEl.style.width = `${width}px`;
          wrapperEl.style.transform = `translateX(${offset}px)`;
        }
      }
    };

    /**
     * Applies a CSS `transition-duration` to every inner wrapper so Swiper's
     * programmatic transitions (e.g. snap-to-slide) animate smoothly.
     * No-ops when `expand` is not active at the current breakpoint.
     *
     * @param {number} duration - Transition duration in milliseconds.
     */
    const setTransition = (duration) => {
      if (!swiper.params.expand) return;

      for (let i = 0; i < swiper.slides.length; i++) {
        const wrapperEl = swiper.slides[i].querySelector(WRAPPER_SELECTOR);
        if (wrapperEl) {
          wrapperEl.style.transitionDuration = `${duration}ms`;
        }
      }
    };

    /**
     * Removes all inline styles written by this module from every inner
     * wrapper. Called when a responsive breakpoint disables the `expand`
     * option so the layout reverts to its CSS-only state.
     */
    const cleanup = () => {
      if (!swiper.slides) return;

      for (let i = 0; i < swiper.slides.length; i++) {
        const wrapperEl = swiper.slides[i].querySelector(WRAPPER_SELECTOR);
        if (wrapperEl) {
          wrapperEl.style.removeProperty("width");
          wrapperEl.style.removeProperty("transform");
          wrapperEl.style.removeProperty("transition-duration");
        }
      }
    };

    on("beforeInit", () => {
      // Enable watchSlidesProgress if expand is configured at any breakpoint
      const hasExpand =
        swiper.params.expand ||
        (swiper.params.breakpoints &&
          Object.values(swiper.params.breakpoints).some((bp) => bp.expand));

      if (!hasExpand) return;

      swiper.params.watchSlidesProgress = true;
      swiper.originalParams.watchSlidesProgress = true;
    });

    on("init", () => {
      setTranslate();
    });

    on("setTranslate", setTranslate);

    on("setTransition", (_s, duration) => {
      setTransition(duration);
    });

    // Clean up inline styles when a breakpoint change disables expand
    on("breakpoint", () => {
      if (!swiper.params.expand) cleanup();
    });
  };

  window.swiperModules = {
    expandModule,
  };
})();
